import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import logo from "../../images/logo.png";
import Select from "react-select";
import { Row, Col, Modal, Button, Badge } from "react-bootstrap";

const YesNoToggle = ({ name, value, onChange, label, disabled }) => {
  return (
    <div>
      <label className="d-block mb-1">{label}</label>
      <div className="d-flex gap-2" role="group">
        <input
          type="radio"
          className="btn-check"
          name={name}
          id={`${name}_yes`}
          value="Yes"
          checked={value === "Yes"}
          onChange={(e) => onChange(name, e.target.value)}
          disabled={disabled}
        />
        <label
          className="btn btn-outline-primary"
          htmlFor={`${name}_yes`}
          style={{ minWidth: "72px" }}
        >
          Yes
        </label>

        <input
          type="radio"
          className="btn-check"
          name={name}
          id={`${name}_no`}
          value="No"
          checked={value === "No"}
          onChange={(e) => onChange(name, e.target.value)}
          disabled={disabled}
        />
        <label
          className="btn btn-outline-secondary"
          htmlFor={`${name}_no`}
          style={{ minWidth: "72px" }}
        >
          No
        </label>
      </div>
    </div>
  );
};

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const INTERVIEW_TASK_ID = 31;
const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const InterviewQuestionsPage = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [readyForReview, setReadyForReview] = useState(false);
  const [isReviewed, setIsReviewed] = useState(false);
  const [isSyncingQuestions, setIsSyncingQuestions] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignmentMode, setAssignmentMode] = useState("programmed");
  const [allQuestions, setAllQuestions] = useState([]);
  const [assignedQuestionIds, setAssignedQuestionIds] = useState([]);
  const [selectedQuestionOption, setSelectedQuestionOption] = useState(null);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);
  const [isSavingAssignments, setIsSavingAssignments] = useState(false);

  const parseOptions = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "object") return Object.values(raw);
    try {
      return JSON.parse(raw);
    } catch {
      return raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
  };

  const fetchQA = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await axios.get(
        `${BASE_URL}/api/interviews/user/${userId}/${INTERVIEW_TASK_ID}`,
        authConfig()
      );
      setQuestions(res.data.questions || []);
      setAnswers(res.data.answers || {});
      setAssignmentMode(res.data.assignmentMode || "programmed");

      if (
        res.data.taskStatus === "review" ||
        res.data.taskStatus === "completed"
      ) {
        setReadyForReview(true);
      } else {
        setReadyForReview(false);
      }
      setIsReviewed(res.data.taskStatus === "completed");
    } catch (err) {
      console.error("Failed to load interview questions + answers", err);
    }
  }, [userId]);

  useEffect(() => {
    fetchQA();
  }, [fetchQA]);

  const loadAssignments = useCallback(async () => {
    if (!isAdmin || !userId) return;

    try {
      setIsLoadingAssignments(true);
      const res = await axios.get(
        `${BASE_URL}/api/interviews/assignments/${userId}/${INTERVIEW_TASK_ID}`,
        authConfig()
      );

      setAllQuestions(res.data.allQuestions || []);
      setAssignedQuestionIds((res.data.assignedQuestionIds || []).map(Number));
      setAssignmentMode(res.data.assignmentMode || "programmed");
      setSelectedQuestionOption(null);
    } catch (err) {
      console.error("Failed to load interview assignments", err);
      alert("Failed to load interview question assignments.");
    } finally {
      setIsLoadingAssignments(false);
    }
  }, [isAdmin, userId]);

  const handleChange = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${BASE_URL}/api/interviews/answers/${userId}/${INTERVIEW_TASK_ID}`,
        { answers },
        authConfig()
      );
      alert("Saved!");
    } catch (err) {
      console.error("Failed to save answers", err);
    }
  };

  const handleReviewToggle = async (e) => {
    const checked = e.target.checked;

    if (checked) {
      if (!window.confirm("Are you sure you want to mark as Ready for Review?")) {
        return;
      }
      try {
        await axios.put(
          `${BASE_URL}/api/interviews/review/${userId}/${INTERVIEW_TASK_ID}`,
          {},
          authConfig()
        );
        setReadyForReview(true);
        setIsReviewed(false);
        alert("Interview marked as Ready for Review!");
      } catch (err) {
        console.error("Failed to mark Ready for Review", err);
        alert("Failed to update Ready for Review status.");
      }
    } else {
      if (!isAdmin) return;
      if (!window.confirm("Reset interview back to Not Completed?")) return;

      try {
        await axios.put(
          `${BASE_URL}/api/interviews/review/reset/${userId}/${INTERVIEW_TASK_ID}`,
          {},
          authConfig()
        );
        setReadyForReview(false);
        setIsReviewed(false);
        alert("Interview reset to Not Completed.");
      } catch (err) {
        console.error("Failed to reset review", err);
        alert("Failed to reset review status.");
      }
    }
  };

  const handleCancel = () => {
    navigate(isAdmin && paramId ? `/userhome/${paramId}` : "/userhome");
  };

  const openAssignQuestionsModal = async () => {
    if (!isAdmin) return;
    setShowAssignModal(true);
    await loadAssignments();
  };

  const handleSyncProgrammedQuestions = async () => {
    if (!isAdmin) return;
    if (
      !window.confirm(
        "Sync the latest programmed interview questions for this user? Answers for removed questions will be deleted."
      )
    ) {
      return;
    }

    try {
      setIsSyncingQuestions(true);
      await axios.post(
        `${BASE_URL}/api/interviews/sync/${userId}/${INTERVIEW_TASK_ID}`,
        {},
        authConfig()
      );
      await loadAssignments();
      await fetchQA();
      alert("Programmed interview questions synced for this user.");
    } catch (err) {
      console.error("Failed to sync interview questions", err);
      alert("Failed to sync the programmed interview questions.");
    } finally {
      setIsSyncingQuestions(false);
    }
  };

  const addAssignedQuestion = () => {
    if (!selectedQuestionOption) return;

    setAssignedQuestionIds((prev) =>
      prev.includes(selectedQuestionOption.value)
        ? prev
        : [...prev, selectedQuestionOption.value]
    );
    setAssignmentMode("manual");
    setSelectedQuestionOption(null);
  };

  const removeAssignedQuestion = (questionId) => {
    setAssignedQuestionIds((prev) => prev.filter((id) => id !== questionId));
    setAssignmentMode("manual");
  };

  const handleSaveAssignments = async () => {
    try {
      setIsSavingAssignments(true);
      await axios.put(
        `${BASE_URL}/api/interviews/assignments/${userId}/${INTERVIEW_TASK_ID}`,
        { questionIds: assignedQuestionIds },
        authConfig()
      );
      await fetchQA();
      setShowAssignModal(false);
      alert("Interview questions updated for this user.");
    } catch (err) {
      console.error("Failed to save interview assignments", err);
      alert("Failed to save interview question assignments.");
    } finally {
      setIsSavingAssignments(false);
    }
  };

  const renderInput = (q) => {
    const value = answers[q.iq_id] ?? "";
    const disabled = isReviewed;

    switch (q.iq_type) {
      case "text":
      case "number":
      case "date":
        return (
          <input
            type={q.iq_type}
            className="form-control"
            value={value}
            onChange={(e) => handleChange(q.iq_id, e.target.value)}
            disabled={disabled}
          />
        );

      case "textarea":
        return (
          <textarea
            className="form-control"
            value={value}
            onChange={(e) => handleChange(q.iq_id, e.target.value)}
            disabled={disabled}
          />
        );

      case "boolean":
        return (
          <YesNoToggle
            name={`q_${q.iq_id}`}
            value={value}
            onChange={(name, v) => handleChange(q.iq_id, v)}
            label={q.iq_question}
            disabled={disabled}
          />
        );

      case "select":
        return (
          <Select
            options={parseOptions(q.iq_options).map((o) => ({
              value: o,
              label: o,
            }))}
            value={value ? { value, label: value } : null}
            onChange={(opt) => handleChange(q.iq_id, opt?.value || "")}
            isDisabled={disabled}
          />
        );

      case "multiselect":
        return (
          <Select
            isMulti
            options={parseOptions(q.iq_options).map((o) => ({
              value: o,
              label: o,
            }))}
            value={
              Array.isArray(value)
                ? value.map((v) => ({ value: v, label: v }))
                : []
            }
            onChange={(opts) =>
              handleChange(
                q.iq_id,
                (opts || []).map((o) => o.value)
              )
            }
            isDisabled={disabled}
          />
        );

      default:
        return null;
    }
  };

  const assignedQuestions = assignedQuestionIds
    .map((id) =>
      allQuestions.find((question) => Number(question.iq_id) === Number(id))
    )
    .filter(Boolean);

  const availableQuestionOptions = allQuestions
    .filter((question) => !assignedQuestionIds.includes(Number(question.iq_id)))
    .map((question) => ({
      value: Number(question.iq_id),
      label: question.iq_question,
    }));

  const formatPrintAnswer = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "—";
    if (value && typeof value === "object") {
      return Object.values(value).filter(Boolean).join(", ") || "—";
    }
    return String(value ?? "").trim() || "—";
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          Interview Questions
        </span>
        <div className="d-flex gap-2">
          {isAdmin && !isReviewed && (
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={openAssignQuestionsModal}
            >
              Assign New Questions
            </button>
          )}
          <button className="btn btn-secondary" onClick={handleCancel}>
            Return
          </button>
        </div>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Interview Questions</h2>
          </div>
          <CompanyAddress />
        </div>

        <form onSubmit={handleSave} data-print-screen-only>
          <Row>
            {questions.map((q) => (
              <Col md={6} key={q.iq_id} className="mb-3">
                {q.iq_type === "boolean" ? (
                  renderInput(q)
                ) : (
                  <>
                    <label className="form-label">{q.iq_question}</label>
                    {renderInput(q)}
                  </>
                )}
              </Col>
            ))}
          </Row>

          <div className="form-check mt-4">
            <input
              type="checkbox"
              className="form-check-input"
              id="readyForReview"
              checked={readyForReview}
              onChange={handleReviewToggle}
              disabled={isReviewed}
            />
            <label className="form-check-label" htmlFor="readyForReview">
              Ready for Review
            </label>
          </div>

          {!isReviewed && (
            <div className="d-flex justify-content-end mt-4">
              <button type="submit" className="btn btn-primary">
                Save Answers
              </button>
            </div>
          )}
        </form>

        <div className="d-none packet-field-grid" data-print-interview-questions>
          {questions.map((question) => (
            <div
              key={`print-${question.iq_id}`}
              className={question.iq_type === "textarea" ? "packet-field packet-field-wide" : "packet-field"}
              data-print-interview-question
              data-long-answer={question.iq_type === "textarea" ? "true" : undefined}
            >
              <div className="packet-field-label" data-print-question-label>{question.iq_question}</div>
              <div className="packet-static-answer" data-print-question-answer>
                {formatPrintAnswer(answers[question.iq_id])}
              </div>
            </div>
          ))}
        </div>

        <hr className="my-5" />

        {userId && (
          <div data-print-screen-only>
          <ReviewManager
            taskName="interview"
            taskId={INTERVIEW_TASK_ID}
            userId={isAdmin && paramId ? paramId : userId}
            isAdmin={isAdmin}
          />
          </div>
        )}
      </div>

      <Modal
        show={showAssignModal}
        onHide={() => !isSavingAssignments && setShowAssignModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton={!isSavingAssignments}>
          <Modal.Title>Assign Interview Questions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingAssignments ? (
            <div>Loading assigned questions...</div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div>
                  <div className="fw-semibold">Assignment Mode</div>
                  <div className="mt-1">
                    <Badge
                      bg="light"
                      text="dark"
                      style={{
                        border: "1px solid",
                        borderColor:
                          assignmentMode === "manual" ? "#f0c36d" : "#cbd5e1",
                        backgroundColor:
                          assignmentMode === "manual" ? "#fff7e6" : "#f8fafc",
                        color: assignmentMode === "manual" ? "#8a5a00" : "#334155",
                      }}
                    >
                      {assignmentMode === "manual" ? "Manual" : "Default"}
                    </Badge>
                  </div>
                </div>
                <Button
                  variant="outline-primary"
                  onClick={handleSyncProgrammedQuestions}
                  disabled={isSyncingQuestions || isSavingAssignments}
                >
                  {isSyncingQuestions ? "Syncing..." : "Sync Questions"}
                </Button>
              </div>

              <div className="mb-3">
                <label className="form-label">Add Question for This User</label>
                <div className="d-flex gap-2">
                  <div style={{ flex: 1 }}>
                    <Select
                      value={selectedQuestionOption}
                      options={availableQuestionOptions}
                      onChange={setSelectedQuestionOption}
                      placeholder="Choose a question..."
                      isDisabled={isSavingAssignments}
                    />
                  </div>
                  <Button
                    variant="success"
                    onClick={addAssignedQuestion}
                    disabled={!selectedQuestionOption || isSavingAssignments}
                  >
                    Add
                  </Button>
                </div>
              </div>

              <div>
                <label className="form-label">Assigned Questions</label>
                {assignedQuestions.length ? (
                  <div className="list-group">
                    {assignedQuestions.map((question) => (
                      <div
                        key={question.iq_id}
                        className="list-group-item d-flex justify-content-between align-items-start gap-3"
                      >
                        <div>
                          <div>{question.iq_question}</div>
                          <small className="text-muted">
                            Type: {question.iq_type}
                          </small>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeAssignedQuestion(Number(question.iq_id))}
                          disabled={isSavingAssignments}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted">
                    No interview questions are currently assigned to this user.
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAssignModal(false)}
            disabled={isSavingAssignments}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveAssignments}
            disabled={isLoadingAssignments || isSavingAssignments}
          >
            {isSavingAssignments ? "Saving..." : "Save Manual Questions"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InterviewQuestionsPage;
